import { OfferContainerProps as CashOutContainerProps, Identifier, UserInfo } from '@types';
import { logUserEvent, logApiEvent } from '@analytics/amplifyLogging';
import { auth, config as publicConfig } from '../config';
import React from 'react';
import { AuthenticatedLeadForms, AuthenticatedLeadFormsProps, EventType, FormatWithMask, mobileMask } from 'shared-ui-components';
import { selectLoan, selectUserInfo } from '@store/selectors/widgets.selectors';
import { useDispatch, useSelector } from 'react-redux';
import { FormWrapper } from '@app.styled';
import { broadcastCashOutResponse } from '@store/actions/widgets.actions';
import { CodingConstants } from '@constants';
import { addressFormat } from '@utils/addressFormat';

export const CashOutContainer: React.FC<CashOutContainerProps> = ({ loanNumber, updateLoanType, offerDetails }) => {
    const dispatch = useDispatch();
    const token = auth.getOkta().getAccessToken();
    const userInfo: UserInfo = useSelector(selectUserInfo);
    const loanList: Identifier[] = useSelector(selectLoan);
    const selectedLoan = loanList.find(loanList => loanList.identifier === loanNumber);
    const { address1, address2, city, state, zip } = selectedLoan.propertyAddress;
    const { email, firstName, lastName, mobile } = userInfo;
    const isPersonalizedCashOut = offerDetails != undefined ? true : false;

    const config: any = {
        metadata: {
            steps: 3,
            type: 'AuthCashOut',
            keyword: 'Cash Out'
        },
        1: {
            screen: 'RadioCardGroup',
            metadata: {
                id: 'visited-home-equity',
                title: 'How would you like to use your home equity?',
                navigation: {
                    current: 1,
                    next: 2,
                    back: 1
                },
                valueKey: 'refinanceReason',
                metadata: [
                    {
                        id: 'debt-consolidation',
                        icon: 'DebtConsolidationIcon',
                        label: 'Debt Consolidation',
                        value: 'Cash Out Debt Consolidation'
                    },
                    {
                        id: 'home-improvement',
                        icon: 'HomeImprovementIcon',
                        label: 'Home Improvement',
                        value: 'Cash Out Home Improvement'
                    },
                    {
                        id: 'other',
                        icon: 'OtherIcon',
                        label: 'Other',
                        value: 'Cash Out Other'
                    }
                ]
            }
        },

        2: {
            id: 2,
            screen: 'WithdrawalAmount',
            metadata: {
                id: 'equity-withdrawal-cash-out',
                title: isPersonalizedCashOut
                    ? `Based on the estimated value of your home, you may have an estimated $${offerDetails?.estCo?.toLocaleString()} or more available to you.`
                    : 'How much cash do you want?',
                valueKey: 'equityCash',
                navigation: {
                    current: 2,
                    next: 3,
                    back: 1
                },
                metadata: {
                    id: 1,
                    equityCash: isPersonalizedCashOut ? offerDetails?.estCo : 548250,
                    isPersonalizedCashOut: isPersonalizedCashOut
                }
            }
        },
        3: {
            screen: 'ReviewInformation',
            metadata: {
                id: 'cashout-review-information',
                title: 'Review and Submit Information',
                navigation: {
                    current: 3,
                    back: 2
                },
                metadata: {
                    reviewInformationList: [
                        {
                            id: 1,
                            title: 'Refinance Reason',
                            caption: ['Debt Consolidation'],
                            isEdit: true,
                            stepNumber: 1,
                            valueKey: ['refinanceReason']
                        },
                        {
                            id: 2,
                            title: 'Equity Cashout',
                            caption: [`$29,876`],
                            isEdit: true,
                            stepNumber: 2,
                            valueKey: ['equityCash'],
                            type: 'dollar'
                        },
                        {
                            id: 3,
                            title: 'Contact Information',
                            caption: [
                                `John Smith`,
                                'john.smith@gmail.com',
                                '212-555-9999',
                                '12000 Government Center Parkway, 202, Fairfax, VA, 22035'
                            ],
                            isEdit: true,
                            stepNumber: 4,
                            valueKey: ['fullName', 'email', 'formattedPhoneNumber', 'propertyInformation']
                        }
                    ]
                }
            }
        },
        4: {
            screen: 'ContactInformation',
            metadata: {
                navigation: {
                    current: 4,
                    back: 3,
                    next: 3
                }
            }
        }
    };
    const cashOutProps: AuthenticatedLeadFormsProps = {
        config,
        accessToken: token,
        logApiEvent: (event: EventType) => {
            logApiEvent({
                apiName: event.apiName,
                action: event.action,
                correlationId: event.correlationId,
                timestamp: event.timestamp,
                loanNumber: event.loanNumber,
                description: event.description,
                type: 'Cashout widget'
            });
        },
        logUserEvent: (event: EventType) => {
            logUserEvent({
                type: 'Cashout widget',
                action: event.action,
                loanNumber: event.loanNumber,
                description: event.description
            });
        },

        metadata: {
            initialState: {
                0: { refinanceType: 'Refinance and Get Cash' },
                1: null,
                2: null,
                3: { propertyInformation: addressFormat(selectedLoan.propertyAddress) },
                4: {
                    fullName: `${firstName} ${lastName}`,
                    formattedPhoneNumber: FormatWithMask({
                        mask: mobileMask,
                        text: mobile
                    }).masked,
                    firstName: firstName,
                    lastName: lastName,
                    email: email,
                    phoneNumber: mobile,
                    propertyInformation: addressFormat(selectedLoan.propertyAddress),
                    address: {
                        street: address1,
                        street2: address2,
                        city: city,
                        state: state,
                        zipCode: zip
                    }
                }
            },
            request: {
                offerGUID: offerDetails?.offerGUID,
                borrowers: [
                    {
                        address: {
                            street: address1,
                            street2: address2,
                            city: city,
                            state: state,
                            zipCode: zip
                        },
                        firstName: firstName,
                        middleName: '',
                        lastName: lastName,
                        nameSuffix: '',
                        email: email,
                        phone: mobile
                    }
                ],
                loanNumber: loanNumber,
                productInfo: {
                    type: 'refinance',
                    refinanceDetails: {
                        type: 'CO',
                        cashoutDetails: {
                            refinanceReason: '',
                            equityCash: isPersonalizedCashOut ? offerDetails?.estCo : 548250
                        }
                    }
                },
                referralSource: 'ACP',
                utmDetails: {
                    utmDevice: 'Web',
                    utmDevicemodel: 'Chrome'
                }
            }
        },

        leadApiParams: {
            baseUrl: publicConfig.env('leadSubmissionUrl'),
            xApiKey: publicConfig.env('xApiKey')
        },

        onComplete: (metadata: any) => {
            updateLoanType(CodingConstants.servicingLoanType);
            dispatch(broadcastCashOutResponse(metadata));
        }
    };
    return (
        <FormWrapper>
            <AuthenticatedLeadForms {...cashOutProps} />
        </FormWrapper>
    );
};
